import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Row, Col, Table, Typography, Segmented, DatePicker, Select,
  Space, Tag, Card, Statistic, Button, Alert, message,
} from 'antd';
import {
  BarChartOutlined, ReloadOutlined, TableOutlined, DownloadOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import dayjs from 'dayjs';
import { servicesAPI } from '../../api/client';
import {
  ALLOCATION_COLORS, ALLOCATION_LABELS, CURRENCY_SYMBOLS,
} from './constants';
import TableToolbar from '../../components/TableToolbar';
import useTableSort from '../../hooks/useTableSort';
import { sortTableRows } from '../../utils/tableRows';
import { makeTablePagination, normalizeTablePagination } from '../../utils/tablePagination';
import { downloadBlobResponse } from '../../utils/downloadBlob';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const PIE_COLORS = ['#4096ff', '#fa8c16'];
const DEFAULT_RANGE = () => [dayjs().subtract(6, 'month'), dayjs()];

function count(value) {
  return Number(value || 0);
}

export default function AnalyticsTab() {
  const [viewMode, setViewMode]           = useState('charts');
  const [dateRange, setDateRange]         = useState(DEFAULT_RANGE());
  const [categoryId, setCategoryId]       = useState(null);
  const [serviceId, setServiceId]         = useState(null);
  const [allocationMode, setAllocationMode] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [pagination, setPagination]       = useState({ current: 1, pageSize: 50 });
  const { sortBy, sortDir, sortIcon, getSortableHeaderProps } = useTableSort({
    initialSortBy: 'current_users',
    initialSortDir: 'desc',
    onSort: () => setPagination(p => ({ ...p, current: 1 })),
  });

  const params = useMemo(() => ({
    date_from:       dateRange?.[0]?.format('YYYY-MM-DD'),
    date_to:         dateRange?.[1]?.format('YYYY-MM-DD'),
    category_id:     categoryId || undefined,
    service_id:      serviceId || undefined,
    allocation_mode: allocationMode || undefined,
  }), [dateRange, categoryId, serviceId, allocationMode]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['services-analytics', params],
    queryFn:  () => servicesAPI.getAnalytics(params).then(r => r.data),
  });

  const { data: servicesList } = useQuery({
    queryKey: ['services', 'analytics-options'],
    queryFn:  () => servicesAPI.getAll({ sort_by: 'name', sort_dir: 'asc' }).then(r => r.data),
  });

  const { data: categories } = useQuery({
    queryKey: ['service-categories'],
    queryFn:  () => servicesAPI.getCategories().then(r => r.data),
  });

  const serviceOptions = useMemo(() => (
    (servicesList || [])
      .filter(service => !categoryId || service.category_id === categoryId)
      .filter(service => !allocationMode || (
        service.allocation_mode === allocationMode && count(service.purchased_groups) > 0
      ))
      .map(service => ({ label: service.name, value: service.id }))
  ), [servicesList, categoryId, allocationMode]);

  const snapshot = data?.snapshot || {};
  const period = data?.period || {};

  const pieData = (data?.type_breakdown || []).map(row => ({
    name: row.license_type === 'purchased' ? 'Купленные лицензии' : 'Грязный домен',
    value: count(row.active_assignments),
  }));

  const monthlyData = (data?.monthly || []).map(row => ({
    month:     row.month,
    Выдано:    count(row.assigned),
    Отозвано:  count(row.revoked),
  }));

  const summaryRows = useMemo(() => sortTableRows(data?.summary || [], {
    sortBy,
    sortDir,
    types: {
      current_users: 'number',
      purchased_users: 'number',
      grey_users: 'number',
      purchased_capacity: 'number',
      assigned_period: 'number',
      revoked_period: 'number',
    },
  }), [data, sortBy, sortDir]);

  const resetFilters = () => {
    setDateRange(DEFAULT_RANGE());
    setCategoryId(null);
    setServiceId(null);
    setAllocationMode(null);
    setPagination(p => ({ ...p, current: 1 }));
  };

  const hasFilters = Boolean(
    categoryId || serviceId || allocationMode
    || !dateRange?.[0]?.isSame(dayjs().subtract(6, 'month'), 'day')
    || !dateRange?.[1]?.isSame(dayjs(), 'day')
  );

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await servicesAPI.exportAnalytics(params);
      downloadBlobResponse(response, 'services-analytics.xlsx');
    } catch (exportError) {
      message.error(exportError?.response?.data?.detail || 'Не удалось скачать аналитику');
    } finally {
      setExportLoading(false);
    }
  };

  const summaryColumns = [
    {
      title: `Сервис${sortIcon('service_name')}`,
      dataIndex: 'service_name', key: 'name', minWidth: 220, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('service_name'),
      render: (value, row) => (
        <Space size={6} wrap>
          <Text strong>{value}</Text>
          {row.category_name && (
            <Tag color={row.category_color} style={{ fontSize: 10 }}>{row.category_name}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: `Тип лицензирования${sortIcon('allocation_mode')}`,
      dataIndex: 'allocation_mode', key: 'allocation', width: 170,
      onHeaderCell: () => getSortableHeaderProps('allocation_mode'),
      render: (value, row) => count(row.purchased_groups) > 0 ? (
        <Tag color={ALLOCATION_COLORS[value] || 'default'}>
          {ALLOCATION_LABELS[value] || value || '—'}
        </Tag>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: `Пользователей всего${sortIcon('current_users')}`,
      dataIndex: 'current_users', key: 'users', width: 150, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('current_users'),
    },
    {
      title: `По лицензиям${sortIcon('purchased_users')}`,
      dataIndex: 'purchased_users', key: 'purchased', width: 125, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('purchased_users'),
      render: value => <Text style={{ color: '#1677ff' }}>{value}</Text>,
    },
    {
      title: `Грязный домен${sortIcon('grey_users')}`,
      dataIndex: 'grey_users', key: 'grey', width: 125, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('grey_users'),
      render: value => count(value) > 0
        ? <Text style={{ color: '#fa8c16' }}>{value}</Text>
        : <Text type="secondary">0</Text>,
    },
    {
      title: `Куплено${sortIcon('purchased_capacity')}`,
      dataIndex: 'purchased_capacity', key: 'capacity', width: 105, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('purchased_capacity'),
    },
    {
      title: `Выдано за период${sortIcon('assigned_period')}`,
      dataIndex: 'assigned_period', key: 'assigned', width: 140, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('assigned_period'),
    },
    {
      title: `Отозвано за период${sortIcon('revoked_period')}`,
      dataIndex: 'revoked_period', key: 'revoked', width: 150, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('revoked_period'),
    },
  ];

  return (
    <div>
      <TableToolbar>
        <Space wrap size={8}>
          <RangePicker
            value={dateRange}
            onChange={value => {
              setDateRange(value);
              setPagination(p => ({ ...p, current: 1 }));
            }}
            format="DD.MM.YYYY"
            presets={[
              { label: '3 месяца', value: [dayjs().subtract(3, 'month'), dayjs()] },
              { label: 'Полгода',  value: [dayjs().subtract(6, 'month'), dayjs()] },
              { label: 'Этот год', value: [dayjs().startOf('year'), dayjs()] },
            ]}
          />
          <Select
            placeholder="Категория" allowClear style={{ width: 180 }}
            value={categoryId}
            onChange={value => {
              setCategoryId(value);
              setServiceId(null);
              setPagination(p => ({ ...p, current: 1 }));
            }}
            options={(categories || []).map(category => ({
              label: category.name,
              value: category.id,
            }))}
          />
          <Select
            placeholder="Тип лицензирования" allowClear style={{ width: 190 }}
            value={allocationMode}
            onChange={value => {
              setAllocationMode(value);
              setServiceId(null);
              setPagination(p => ({ ...p, current: 1 }));
            }}
            options={Object.entries(ALLOCATION_LABELS).map(([value, label]) => ({ label, value }))}
          />
          <Select
            placeholder="Сервис" allowClear showSearch optionFilterProp="label" style={{ width: 220 }}
            value={serviceId}
            onChange={value => {
              setServiceId(value);
              setPagination(p => ({ ...p, current: 1 }));
            }}
            options={serviceOptions}
          />
          {hasFilters && <Button onClick={resetFilters}>Сбросить</Button>}
          <Button icon={<DownloadOutlined />} loading={exportLoading} onClick={handleExport}>
            Экспорт Excel
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Обновить</Button>
        </Space>
      </TableToolbar>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Как считается аналитика"
        description="Период влияет на показатели «Выдано», «Отозвано» и расходы. Пользователи, активные доступы и купленные лицензии показывают текущее состояние на сегодня. Расходы считаются только по истории закупок; технический opening balance не считается стоимостью."
      />

      {isError && (
        <Alert
          type="error" showIcon style={{ marginBottom: 16 }}
          message="Не удалось загрузить аналитику сервисов"
          description={typeof error?.response?.data?.detail === 'string'
            ? error.response.data.detail
            : (error?.message || 'Ошибка запроса')}
        />
      )}

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={8} xl={5}>
          <Card size="small"><Statistic title="Сервисов в выборке" value={count(snapshot.services_count)} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={5}>
          <Card size="small"><Statistic title="Пользователей сейчас" value={count(snapshot.users_count)} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={5}>
          <Card size="small"><Statistic title="Активных доступов" value={count(snapshot.active_assignments)} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={5}>
          <Card size="small"><Statistic title="Куплено лицензий" value={count(snapshot.purchased_capacity)} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card size="small"><Statistic title="Грязный домен" value={count(snapshot.grey_assignments)} /></Card>
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small"><Statistic title="Выдано за период" value={count(period.assigned)} /></Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small"><Statistic title="Отозвано за период" value={count(period.revoked)} /></Card>
        </Col>
        {(data?.costs || []).map(cost => (
          <Col xs={24} sm={12} lg={6} key={cost.currency}>
            <Card size="small">
              <Statistic
                title={`Расходы ${cost.currency}`}
                value={cost.total_cost == null ? '—' : Number(cost.total_cost)}
                suffix={cost.total_cost == null ? undefined : (CURRENCY_SYMBOLS[cost.currency] || cost.currency)}
                precision={cost.total_cost == null ? undefined : 2}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {count(cost.purchases_count)} закупок · {count(cost.priced_purchases_count)} с ценой · {count(cost.quantity)} лицензий
              </Text>
            </Card>
          </Col>
        ))}
      </Row>

      <Segmented
        value={viewMode} onChange={setViewMode}
        options={[
          { label: <Space><BarChartOutlined />Графики</Space>, value: 'charts' },
          { label: <Space><TableOutlined />Таблица</Space>, value: 'table'  },
        ]}
        style={{ marginBottom: 16 }}
      />

      {viewMode === 'table' ? (
        <Card size="small">
          <Table
            dataSource={summaryRows} columns={summaryColumns}
            loading={isLoading} rowKey="service_id" size="small" scroll={{ x: 1200 }}
            pagination={makeTablePagination({
              ...pagination,
              total: summaryRows.length,
              showTotal: total => `${total} сервисов`,
            })}
            onChange={pag => setPagination(normalizeTablePagination(pag))}
          />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <Card size="small" title="Выдано и отозвано по месяцам">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthlyData} margin={{ left: 0, right: 16, top: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <ReTooltip />
                  <Legend />
                  <Bar dataKey="Выдано" fill="#4096ff" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="Отозвано" fill="#ff4d4f" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card size="small" title="Активные доступы по типу">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    outerRadius={90}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((_, index) => (
                      <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <ReTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24}>
            <Card size="small" title="Топ сервисов по пользователям сейчас">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={[...(data?.top_services || [])]
                    .reverse()
                    .map(row => ({ name: row.service_name, value: count(row.active_users) }))}
                  layout="vertical" margin={{ left: 16, right: 40, top: 4, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12 }} />
                  <ReTooltip formatter={value => [value, 'Пользователей']} />
                  <Bar
                    dataKey="value" fill="#52c41a" radius={[0, 4, 4, 0]}
                    label={{ position: 'right', fontSize: 11 }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
}
